import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, BookOpen, Layers, ListMusic, Fingerprint } from "lucide-react";
import { scrollToSection } from "../lib/experience";
import InTheVault from "./InTheVault";
import Tracklist from "./Tracklist";
import OperationPhillyFreeway from "./OperationPhillyFreeway";
import BookletViewer from "./BookletViewer";

// Master accordion that wraps the four chapters of the anniversary experience.
// Chapters (each retains its own internal drawer state and anchor id):
//   01 · The Vault           (#vault)
//   02 · Tracklist           (#tracklist)
//   03 · Album Booklet       (#booklet) — new interactive spread reader
//   04 · Operation Philly Fw (#operation)
//
// The master accordion (id=#experience) opens/closes the whole stack. The
// /album deep-link, Hero button, and Welcome popup CTA all scroll to
// #experience and auto-open the master via the `open-experience` event.

const CHAPTERS = [
  { n: "01", label: "The Vault",                       target: "vault",     icon: Layers },
  { n: "02", label: "Tracklist",                       target: "tracklist", icon: ListMusic },
  { n: "03", label: "Album Booklet",                   target: "booklet",   icon: BookOpen },
  { n: "04", label: "Operation Philadelphia Freeway",  target: "operation", icon: Fingerprint },
];

export default function AlbumExperience() {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);

  // Auto-open on the /album deep-link or when another component dispatches
  // an `open-experience` event.
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-experience", openHandler);
    if (typeof window !== "undefined" && window.location.hash === "#experience-open") {
      setOpen(true);
    }
    return () => window.removeEventListener("open-experience", openHandler);
  }, []);

  const jumpTo = (id) => {
    setOpen(true);
    // Wait for the accordion to render before scrolling
    setTimeout(() => scrollToSection(id), 260);
  };

  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="relative bg-black text-white"
    >
      <div className="px-4 md:px-10 max-w-[1400px] mx-auto pt-16 md:pt-24 pb-6 md:pb-10">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-3">
          The 23rd Anniversary Experience
        </div>
        <h2
          data-testid="experience-heading"
          className="font-wide text-white uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.95]"
        >
          The Archives
        </h2>
        <p
          data-testid="experience-description"
          className="mt-5 font-body text-white/70 text-base md:text-lg leading-relaxed max-w-3xl"
        >
          Enter the archive, page through the original booklet, revisit the
          tracklist, and discover every fingerprint on Philadelphia Freeway.
        </p>

        {/* Chapter chips — jump into a chapter (opens the master too) */}
        <ul
          data-testid="experience-nav"
          className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
        >
          {CHAPTERS.map(({ n, label, target, icon: Icon }) => (
            <li key={target}>
              <button
                data-testid={`experience-nav-${target}`}
                onClick={() => jumpTo(target)}
                className="w-full h-full flex items-baseline gap-3 border border-white/15 hover:border-[#F4C10A] hover:text-[#F4C10A] text-left px-4 py-4 transition-colors group"
              >
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#F4C10A] group-hover:text-[#FFD22C]">
                  {n}
                </span>
                <span className="flex-1 min-w-0 font-wide uppercase text-white text-sm md:text-base leading-tight group-hover:text-[#F4C10A]">
                  {label}
                </span>
                <Icon className="w-4 h-4 text-white/40 group-hover:text-[#F4C10A] flex-shrink-0" />
              </button>
            </li>
          ))}
        </ul>

        {/* Master toggle */}
        <button
          data-testid="experience-master-toggle"
          aria-expanded={open}
          aria-controls="experience-master-content"
          onClick={() => setOpen((o) => !o)}
          className="mt-8 md:mt-10 w-full flex items-center justify-center gap-3 bg-[#F4C10A] text-black font-display uppercase text-sm tracking-[0.22em] py-4 hover:bg-[#FFD22C] transition-colors"
        >
          {open ? "Close the Experience" : "Open the Experience"}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Master collapsible content — four chapters stacked. Each chapter
          keeps its own inner drawer/state; the master just controls whether
          the whole stack is mounted/visible. */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="experience-master-content"
            id="experience-master-content"
            ref={contentRef}
            data-testid="experience-master-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
            className="overflow-hidden"
          >
            {/* Chapter 01 · Vault */}
            <InTheVault />
            {/* Chapter 02 · Tracklist */}
            <Tracklist />
            {/* Chapter 03 · Album Booklet — new */}
            <BookletChapter />
            {/* Chapter 04 · Operation Philadelphia Freeway */}
            <OperationPhillyFreeway />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// Booklet chapter — matches the visual rhythm of the other three chapters
// (dark background, gold kicker, wide headline) so it feels native inside
// the master accordion.
function BookletChapter() {
  return (
    <section
      id="booklet"
      data-testid="booklet-section"
      className="relative bg-[#0A0A0A] text-white overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative px-4 md:px-10 max-w-[1400px] mx-auto pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="mb-8 max-w-3xl relative z-10">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-3">
            The Original Booklet · Roc-A-Fella · 2003
          </div>
          <h2
            data-testid="booklet-heading"
            className="font-wide text-white uppercase text-4xl sm:text-5xl md:text-6xl"
          >
            The Album Booklet
          </h2>
        </div>
        <BookletViewer />
      </div>
    </section>
  );
}
